import { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/apiClient';
import { getCurrentUser } from '../utils/auth';

const EMPTY = {
  studentId: null,
  items: [],
  totalPending: 0,
  paymentEnabled: false,
  qrConfig: { enabled: false },
};

/** Whether the institute takes fees owed by UPI QR (`/qr-payments/config`). */
export const qrTakesFees = (qrConfig) => !!qrConfig?.enabled && qrConfig.feeDues !== false;

/**
 * What the student owes, and how they can pay it.
 *
 * Two ways in: the card gateway (`paymentEnabled`, Razorpay) and the
 * institute's UPI QR (`qrConfig`). `canPay` is true when EITHER works — every
 * "Pay fees" surface reads it, so an institute with only a QR still gets a pay
 * button everywhere. It used to mean the gateway alone, which hid the top-nav
 * menu and the dashboard card from every QR-only institute.
 */
export default function useStudentPendingFees({ enabled = true } = {}) {
  const user = getCurrentUser();
  const isStudent = user?.usertype === 'student';
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(Boolean(enabled && isStudent));

  const refresh = useCallback(async () => {
    if (!isStudent) {
      setData(EMPTY);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [feesRes, configRes, qrRes] = await Promise.all([
        apiClient.get('/student/me/pending-fees'),
        apiClient.get('/payments/config').catch(() => ({ data: { enabled: false } })),
        // Plan-gated (feeManagement): a 403 simply means no QR.
        apiClient.get('/qr-payments/config').catch(() => ({ data: { data: { enabled: false } } })),
      ]);
      setData({
        studentId: feesRes?.data?.studentId || user.studentId || null,
        items: feesRes?.data?.items || [],
        totalPending: feesRes?.data?.totalPending || 0,
        paymentEnabled: !!configRes?.data?.enabled,
        qrConfig: qrRes?.data?.data || { enabled: false },
      });
    } catch {
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [isStudent, user?.studentId]);

  useEffect(() => {
    if (!enabled || !isStudent) {
      setLoading(false);
      return;
    }
    refresh();
  }, [enabled, isStudent, refresh]);

  const qrForFees = qrTakesFees(data.qrConfig);
  return {
    ...data,
    loading,
    refresh,
    qrForFees,
    hasPending: data.totalPending > 0,
    canPay: data.totalPending > 0 && (data.paymentEnabled || qrForFees),
  };
}
